import * as Sentry from "@sentry/browser";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { installDomGuards } from "./lib/dom-guards";
import { estBruitTiers } from "./lib/sentry-noise";

// Blinde Node.removeChild/insertBefore contre les mutations DOM des tiers (Google
// Translate, Zoho SalesIQ) qui cassaient React avec « removeChild NotFoundError »
// (Sentry BIZCOACH-1M). À poser avant tout render.
installDomGuards();

// Polices auto-hébergées : on lie /fonts/fonts.css au runtime (le bundler Bun
// ne sait pas résoudre un path serveur dans index.html — même contrainte que le
// manifest ci-dessous). font-display:swap → le texte s'affiche immédiatement en
// police de repli puis bascule sur Inter/Sora. Injecté tôt pour minimiser le swap.
if (typeof document !== "undefined" && !document.querySelector('link[data-fonts]')) {
  const fontsLink = document.createElement("link");
  fontsLink.rel = "stylesheet";
  fontsLink.href = "/fonts/fonts.css";
  fontsLink.setAttribute("data-fonts", "");
  document.head.appendChild(fontsLink);
}

fetch("/api/config").then(r => r.json()).then((config: any) => {
  if (config.sentryDsn) {
    Sentry.init({
      dsn: config.sentryDsn,
      // Tunnel same-origin : les events transitent par /api/monitoring (notre
      // domaine) au lieu d'aller directement à l'ingest Sentry. Évite d'exposer
      // le vendor/la région EU dans la CSP et contourne les ad-blockers.
      // Cf. src/api/sentry-tunnel.ts.
      tunnel: "/api/monitoring",
      // L'environnement vient du serveur (APP_ENV, servi par /api/config), comme pour
      // le Sentry backend. Avant, il etait DEDUIT du hostname : tout ce qui n'etait pas
      // localhost etait tague "production" -> une erreur survenue sur dev/staging/demo
      // arrivait dans Sentry sous l'etiquette "production" (issue BIZCOACH-1P, 27/07/2026 :
      // crash TrainingPage tague production alors que le code fautif n'a JAMAIS ete en prod).
      // Repli sur l'ancien comportement si un serveur pas encore deploye ne renvoie pas appEnv.
      environment: config.appEnv || (location.hostname === "localhost" ? "development" : "production"),
      tracesSampleRate: 0.2,
      // Bruit tiers : on n'envoie pas ce qui ne vient pas de notre code.
      // Extensions du navigateur, widget de chat, snippet tapé dans la console —
      // douze des vingt-cinq issues ouvertes le 05/08 étaient dans ce cas, et
      // elles noyaient les vraies. Une seule frame `src/…` suffit à conserver
      // l'événement. Cf. src/lib/sentry-noise.ts pour la règle et ses prudences.
      beforeSend: (event) => (estBruitTiers(event, location.origin) ? null : event),
    });
  }
}).catch(() => {});

// PWA : injection dynamique du link manifest (Bun bundler refuse de résoudre
// un path serveur dans index.html, donc on le fait côté runtime).
if (typeof document !== "undefined" && !document.querySelector('link[rel="manifest"]')) {
  const link = document.createElement("link");
  link.rel = "manifest";
  link.href = "/manifest.json";
  document.head.appendChild(link);
}

// PWA : enregistrement du service worker (web push uniquement — pas de cache d'assets).
// Active uniquement en HTTPS prod / dev en localhost. Pas de blocage si échec.
if ("serviceWorker" in navigator && (location.protocol === "https:" || location.hostname === "localhost")) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => { /* noop */ });
  });
}

// Auto-reload quand un nouveau build est déployé (fini les hard reloads manuels).
import("./lib/app-version-check").then((m) => m.startVersionCheck()).catch(() => { /* noop */ });

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
